import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import toast from 'react-hot-toast';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays, subMonths, isWithinInterval, parseISO } from 'date-fns';
import { getEkycStatusLabel, getPkycStatusLabel } from '../constants/kycStatus';
import AddQueryModal from '../components/AddQueryModal';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import EditQueryModal from '../components/EditQueryModal';
import * as XLSX from 'xlsx';
import jwtDecode from 'jwt-decode';
import Setup2FAModal from '../components/Setup2FAModal';

import { playSuccessSound, playErrorSound } from '../utils/sound';


interface QueryStats {
  total: number;
  resolved: number;
  unsolved: number;
  pending: number;
  enquiry: number;
}

interface QuerySummary {
  today: QueryStats;
  yesterday: QueryStats;
  thisWeek: QueryStats;
  thisMonth: QueryStats;
  lastMonth: QueryStats;
}

interface Query {
  id: string;
  userMobileNumber: string;
  query: string;
  response: string;
  status: 'pending' | 'resolved' | 'unsolved' | 'enquiry';
  remarks: string;
  createdBy: {
    name: string;
    mobileNumber: string;
  };
  updatedBy: {
    name: string;
    mobileNumber: string;
  };
  createdAt: string;
  updatedAt: string;
  userName?: string;
  userType?: string;
  city?: string;
  ekycStatus?: string;
  pkycStatus?: string;
}

interface Staff {
  _id: string;
  name: string;
  mobileNumber: string;
  role: string;
  isActive: boolean;
}

// Helper functions for formatting
function capitalizeFirst(str: string) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

function capitalizeWords(str: string) {
  if (!str) return '';
  return str
    .split(' ')
    .map(word => capitalizeFirst(word))
    .join(' ');
}

function capitalizeAfterDot(str: string) {
  if (!str) return '';
  // Capitalize first letter of the string
  let result = str.charAt(0).toUpperCase() + str.slice(1);
  // Capitalize first letter after every dot
  result = result.replace(/\.\s*([a-zA-Z])/g, (match, p1) => `. ${p1.toUpperCase()}`);
  return result;
}

function formatLocal(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

const Dashboard: React.FC = () => {
  const { user, logout, updateDOB } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<QuerySummary>({
    today: { total: 0, resolved: 0, unsolved: 0, pending: 0, enquiry: 0 },
    yesterday: { total: 0, resolved: 0, unsolved: 0, pending: 0, enquiry: 0 },
    thisWeek: { total: 0, resolved: 0, unsolved: 0, pending: 0, enquiry: 0 },
    thisMonth: { total: 0, resolved: 0, unsolved: 0, pending: 0, enquiry: 0 },
    lastMonth: { total: 0, resolved: 0, unsolved: 0, pending: 0, enquiry: 0 },
  });
  const [queries, setQueries] = useState<Query[]>([]);
  const [searchMobile, setSearchMobile] = useState('');
  const [showAddQuery, setShowAddQuery] = useState(false);
  const [newQuery, setNewQuery] = useState('');
  const [newUserMobile, setNewUserMobile] = useState('');
  const [newResponse, setNewResponse] = useState('');
  const [newStatus, setNewStatus] = useState<'pending' | 'resolved' | 'unsolved'>('pending');
  const [newRemarks, setNewRemarks] = useState('');
  const [loading, setLoading] = useState(true);
  const [showStaffMgmt, setShowStaffMgmt] = useState(false);
  const [showQRMgmt, setShowQRMgmt] = useState(false);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [userMobileError, setUserMobileError] = useState('');
  const [queryError, setQueryError] = useState('');
  const [userInfo, setUserInfo] = useState<any>(null);
  const [userInfoLoading, setUserInfoLoading] = useState(false);
  const [userInfoError, setUserInfoError] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editQuery, setEditQuery] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [commonQueries, setCommonQueries] = useState<{ _id?: string; query: string; response: string }[]>([]);
  const [newCommonQuery, setNewCommonQuery] = useState('');
  const [newCommonResponse, setNewCommonResponse] = useState('');
  const [editingCQ, setEditingCQ] = useState<{ _id: string; query: string; response: string } | null>(null);
  const [editingStaff, setEditingStaff] = useState<Staff | null>(null);
  const [allLastMonthQueries, setAllLastMonthQueries] = useState<Query[]>([]);
  const [selectedQueries, setSelectedQueries] = useState<string[]>([]);
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [showDOBPrompt, setShowDOBPrompt] = useState(false);


  useEffect(() => {
    fetchSummary();
    fetchCommonQueries();
    fetchStaffList();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchQueries();
    // eslint-disable-next-line
  }, [searchMobile]);

  // Auto-refresh queries every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchQueries();
    }, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, []);

  // Auto-refresh summary cards every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      fetchSummary();
    }, 300000); // 5 minutes
    return () => clearInterval(interval);
  }, []);

  // Ensure fetchQueries is called when rowsPerPage or page changes
  useEffect(() => {
    fetchQueries();
    // eslint-disable-next-line
  }, [rowsPerPage, page]);

  useEffect(() => {
    if (user && !user.date_of_birth) {
      setShowDOBPrompt(true);
    }
  }, [user]);

  const handleDOBSubmit = async (dob: string) => {
    try {
      await updateDOB(dob);
      setShowDOBPrompt(false);
      logout();
      toast.success('Date of birth saved!');
    } catch (err: any) {
      toast.error(err.message || 'Failed to save date of birth');
    }
  };

  const fetchSummary = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await api.get('/queries/summary', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(response.data);
    } catch (error) {
      toast.error('Failed to fetch summary');
      playErrorSound();
    }
  };

  const fetchQueries = async (append = false) => {
    try {
      const token = sessionStorage.getItem('token');
      const lastMonthStart = startOfMonth(subMonths(new Date(), 1));
      const now = new Date();
      const response = await api.get('/queries', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: lastMonthStart.toISOString(),
          endDate: now.toISOString(),
          page,
          limit: rowsPerPage,
        },
      });
      const newQueries = response.data.queries.map((q: any) => ({ ...q, id: q._id }));
      if (append) {
        setQueries(prev => [...prev, ...newQueries]);
      } else {
        setQueries(newQueries);
      }
      setTotalPages(response.data.totalPages);
    } catch (error) {
      toast.error('Failed to fetch queries');
      playErrorSound();
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreData = () => {
    setPage(prev => prev + 1);
  };

  // Staff Management (admin only)
  const fetchStaffList = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const res = await api.get('/staff', { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      if (res.data && res.data.staffs) {
        setStaffList(res.data.staffs);
      }
    } catch (error) {
      console.error('Failed to fetch staff list:', error);
    }
  };

  // Fetch user info from external API
  const fetchUserInfo = async (mobile: string) => {
    setUserInfo(null);
    setUserInfoError('');
    if (!/^[0-9]{10}$/.test(mobile)) return;
    setUserInfoLoading(true);
    try {
      const response = await api.post('/proxy/member-details', { mobile_number: mobile });
      setUserInfo(response.data.data || response.data);
    } catch (err: any) {
      setUserInfoError('User not found or API error');
    } finally {
      setUserInfoLoading(false);
    }
  };

  // Real-time validation handlers
  const handleUserMobileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewUserMobile(e.target.value);
    if (!/^[0-9]{10}$/.test(e.target.value)) {
      setUserMobileError('Please enter a valid 10-digit mobile number');
      setUserInfo(null);
    } else {
      setUserMobileError('');
      fetchUserInfo(e.target.value);
    }
  };

  const handleQueryDropdown = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setNewQuery(e.target.value);
    const found = commonQueries.find((q) => q.query === e.target.value);
    setNewResponse(found ? found.response : '');
    setQueryError(!e.target.value.trim() ? 'Query field is required' : '');
  };

  const isFormValid =
    /^[0-9]{10}$/.test(newUserMobile) &&
    !!newQuery.trim() &&
    !userMobileError &&
    !queryError;

  const handleAddQuery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      toast.error('Please fill all required fields correctly');
      playErrorSound();
      return;
    }
    try {
      const token = sessionStorage.getItem('token');
      await api.post('/queries', {
        userMobileNumber: newUserMobile,
        query: newQuery,
        response: newResponse,
        status: newStatus,
        remarks: newRemarks,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Successfully Submitted');
      playSuccessSound();
      setShowAddQuery(false);
      setNewQuery('');
      setNewUserMobile('');
      setNewResponse('');
      setNewStatus('pending');
      setNewRemarks('');
      setUserMobileError('');
      setQueryError('');
      fetchQueries();
    } catch (error: any) {
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(error.response.data.message);
        playErrorSound();
      } else {
        toast.error('Failed to add query');
        playErrorSound();
      }
    }
  };

  const now = new Date();
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);
  const yesterdayStart = startOfDay(subDays(now, 1));
  const yesterdayEnd = endOfDay(subDays(now, 1));
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
  const monthStart = startOfMonth(now);
  const monthEnd = endOfMonth(now);
  const lastMonthStart = startOfMonth(subMonths(now, 1));
  const lastMonthEnd = endOfMonth(subMonths(now, 1));

  const openEditModal = (query: any) => {
    setEditQuery(query);
    setEditModalOpen(true);
  };

  const handleEditSave = async (updatedQuery: any) => {
    try {
      const token = sessionStorage.getItem('token');
      const patchData: any = {
        status: updatedQuery.status,
        remarks: updatedQuery.remarks,
      };
      await api.patch(
        `/queries/${updatedQuery.id}`,
        patchData,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success('Query updated successfully');
      playSuccessSound();
      setEditModalOpen(false);
      fetchQueries();
      fetchSummary();
    } catch (error) {
      toast.error('Failed to update query');
      playErrorSound();
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this query?')) {
      return;
    }
    try {
      const token = sessionStorage.getItem('token');
      await api.delete(`/queries/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Query deleted successfully');
      playSuccessSound();
      setEditModalOpen(false);
      fetchQueries();
    } catch (error) {
      toast.error('Failed to delete query');
      playErrorSound();
    }
  };

  const fetchCommonQueries = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const res = await api.get('/common-queries', { headers: { Authorization: `Bearer ${token}` } });
      setCommonQueries(res.data.queries);
    } catch {
      // No toast here, so no sound
    }
  };

  useEffect(() => { if (showQRMgmt) fetchCommonQueries(); }, [showQRMgmt]);

  const filteredQueries = queries.filter(q => {
    const search = searchMobile.toLowerCase();
    return (
      q.userMobileNumber?.toLowerCase().includes(search) ||
      q.userName?.toLowerCase().includes(search) ||
      q.userType?.toLowerCase().includes(search) ||
      q.city?.toLowerCase().includes(search) ||
      q.query?.toLowerCase().includes(search) ||
      q.response?.toLowerCase().includes(search) ||
      q.remarks?.toLowerCase().includes(search) ||
      q.status?.toLowerCase().includes(search) ||
      q.createdBy?.name?.toLowerCase().includes(search) ||
      q.createdBy?.mobileNumber?.toLowerCase().includes(search) ||
      q.updatedBy?.name?.toLowerCase().includes(search) ||
      q.updatedBy?.mobileNumber?.toLowerCase().includes(search)
    );
  });

  // Bulk selection handlers
  const isAllSelected = filteredQueries.length > 0 && selectedQueries.length === filteredQueries.length;

  const handleSelectQuery = (id: string) => {
    setSelectedQueries(prev => prev.includes(id) ? prev.filter(qid => qid !== id) : [...prev, id]);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedQueries([]);
    } else {
      setSelectedQueries(filteredQueries.map(q => q.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedQueries.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedQueries.length} selected queries?`)) return;
    try {
      const token = sessionStorage.getItem('token');
      await api.post('/queries/bulk',
        { ids: selectedQueries },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Selected queries deleted successfully');
      playSuccessSound();
      setSelectedQueries([]);
      fetchQueries();
    } catch (error) {
      toast.error('Failed to delete selected queries');
      playErrorSound();
    }
  };

  // Update allStats calculation to use all queries
  const allStats = (arr: Query[]) => {
    const total = arr.length;
    const resolved = arr.filter(q => q.status === 'resolved').length;
    const unsolved = arr.filter(q => q.status === 'unsolved').length;
    const pending = arr.filter(q => q.status === 'pending').length;
    const enquiry = arr.filter(q => q.status === 'enquiry').length;
    return { total, resolved, unsolved, pending, enquiry };
  };
  const todayStats = summary.today;
  const yesterdayStats = summary.yesterday;
  const weekStats = summary.thisWeek;
  const monthStats = summary.thisMonth;
  const lastMonthStats = summary.lastMonth;

  // Helper to get staff query counts (last month, from all queries)
  const staffQueryCounts = React.useMemo(() => {
    const counts: Record<string, number> = {};
    const lastMonthStart = startOfMonth(subMonths(new Date(), 1));
    const now = new Date();
    allLastMonthQueries.forEach(q => {
      if (q.createdBy && q.createdBy.name && q.createdAt) {
        const createdAt = new Date(q.createdAt);
        if (createdAt >= lastMonthStart && createdAt <= now) {
          counts[q.createdBy.name] = (counts[q.createdBy.name] || 0) + 1;
        }
      }
    });
    return counts;
  }, [allLastMonthQueries]);

  // Helper to get active staff names (fallback to unique names in allLastMonthQueries if staffList is empty)
  const activeStaffNames = React.useMemo(() => {
    if (staffList.length > 0) {
      return staffList.filter(s => s.isActive).map(s => s.name);
    } else {
      // fallback: get unique names from allLastMonthQueries
      const names = Array.from(new Set(allLastMonthQueries.map(q => q.createdBy?.name).filter(Boolean)));
      return names;
    }
  }, [staffList, allLastMonthQueries]);

  useEffect(() => {
    // Fetch all queries for last month, no pagination
    const fetchAll = async () => {
      const token = sessionStorage.getItem('token');
      const lastMonthStart = startOfMonth(subMonths(new Date(), 1));
      const now = new Date();
      const response = await api.get('/queries', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          startDate: lastMonthStart.toISOString(),
          endDate: now.toISOString(),
          page: 1,
          limit: 10000, // large number to get all
        },
      });
      setAllLastMonthQueries(response.data.queries);
    };
    fetchAll();
  }, [staffList]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100 flex flex-col">
      {/* Topbar */}
      <div className="w-full bg-white shadow flex items-center justify-between px-8 py-4 fixed top-0 left-0 right-0 z-50">
        {/* Left: Logo */}
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Logo" className="h-10 w-auto" />
        </div>
        {/* Center: App Name */}
        <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
          <span className="text-2xl font-extrabold text-gray-900 text-center whitespace-nowrap">Query Management System</span>
        </div>
        {/* Right: Welcome, user and profile */}
        <div className="flex items-center gap-4">
          <span className="font-semibold text-gray-700">Welcome, {user?.name || user?.mobileNumber || 'User'}!</span>
          <div className="relative">
            <button
              className="flex items-center gap-2 px-3 py-2 bg-gray-100 rounded-full hover:bg-gray-200 focus:outline-none"
              onClick={() => setProfileMenuOpen((open) => !open)}
            >
              <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" /><path d="M6 20c0-2.21 3.58-4 6-4s6 1.79 6 4" /></svg>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
            </button>
            {profileMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white border rounded shadow-lg z-50">
                {user?.role === 'admin' && (
                  <>
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                      onClick={() => { setShowStaffMgmt(true); setShowQRMgmt(false); setProfileMenuOpen(false); fetchStaffList(); }}
                    >
                      Staff Management
                    </button>
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                      onClick={() => { setShowQRMgmt(true); setShowStaffMgmt(false); setProfileMenuOpen(false); }}
                    >
                      Q&amp;R Management
                    </button>
                    <button
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                      onClick={() => { navigate('/api-records'); setProfileMenuOpen(false); }}
                    >
                      API Records
                    </button>
                    <hr className="my-1" />
                  </>
                )}
                {/* 2FA and Excel Generator available for all users */}
                <button
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  onClick={() => { setShow2FAModal(true); setProfileMenuOpen(false); }}
                >
                  Setup 2FA (Google Authenticator)
                </button>
                <button
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  onClick={() => { navigate('/excel-generator'); setProfileMenuOpen(false); }}
                >
                  Excel Generator
                </button>
                <hr className="my-1" />
                <button
                  className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                  onClick={() => { logout(); window.location.href = '/login'; }}
                >
                  Logout
                </button>
                <div className="block w-full text-left px-4 py-2 text-gray-400 cursor-not-allowed">Profile (coming soon)</div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-1">
        {/* Main Content (no sidebar) */}
        <main className="flex-1 p-6 pt-24">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-0 mb-4">
            <div className="card bg-gradient-to-br from-blue-100 to-blue-200 border-l-4 border-blue-500 shadow-md">
              <div className="text-xs text-blue-700 font-semibold mb-1">Today</div>
              <div className="text-2xl font-bold text-blue-900">{todayStats.total}</div>
              <div className="flex gap-2 mt-1 items-center">
                <span className="text-green-700">✔ {todayStats.resolved}</span>
                <span className="text-yellow-700">⏳ {todayStats.pending}</span>
                <span className="text-red-700">✖ {todayStats.unsolved}</span>
                <span className="text-blue-700">🛈 {todayStats.enquiry}</span>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-purple-100 to-purple-200 border-l-4 border-purple-500 shadow-md">
              <div className="text-xs text-purple-700 font-semibold mb-1">Yesterday</div>
              <div className="text-2xl font-bold text-purple-900">{yesterdayStats.total}</div>
              <div className="flex gap-2 mt-1 items-center">
                <span className="text-green-700">✔ {yesterdayStats.resolved}</span>
                <span className="text-yellow-700">⏳ {yesterdayStats.pending}</span>
                <span className="text-red-700">✖ {yesterdayStats.unsolved}</span>
                <span className="text-blue-700">🛈 {yesterdayStats.enquiry}</span>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-green-100 to-green-200 border-l-4 border-green-500 shadow-md">
              <div className="text-xs text-green-700 font-semibold mb-1">This Week</div>
              <div className="text-2xl font-bold text-green-900">{weekStats.total}</div>
              <div className="flex gap-2 mt-1 items-center">
                <span className="text-green-700">✔ {weekStats.resolved}</span>
                <span className="text-yellow-700">⏳ {weekStats.pending}</span>
                <span className="text-red-700">✖ {weekStats.unsolved}</span>
                <span className="text-blue-700">🛈 {weekStats.enquiry}</span>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-yellow-100 to-yellow-200 border-l-4 border-yellow-500 shadow-md">
              <div className="text-xs text-yellow-700 font-semibold mb-1">This Month</div>
              <div className="text-2xl font-bold text-yellow-900">{monthStats.total}</div>
              <div className="flex gap-2 mt-1 items-center">
                <span className="text-green-700">✔ {monthStats.resolved}</span>
                <span className="text-yellow-700">⏳ {monthStats.pending}</span>
                <span className="text-red-700">✖ {monthStats.unsolved}</span>
                <span className="text-blue-700">🛈 {monthStats.enquiry}</span>
              </div>
            </div>
            <div className="card bg-gradient-to-br from-pink-100 to-pink-200 border-l-4 border-pink-500 shadow-md">
              <div className="text-xs text-pink-700 font-semibold mb-1">Last Month</div>
              <div className="text-2xl font-bold text-pink-900">{lastMonthStats.total}</div>
              <div className="flex gap-2 mt-1 items-center">
                <span className="text-green-700">✔ {lastMonthStats.resolved}</span>
                <span className="text-yellow-700">⏳ {lastMonthStats.pending}</span>
                <span className="text-red-700">✖ {lastMonthStats.unsolved}</span>
                <span className="text-blue-700">🛈 {lastMonthStats.enquiry}</span>
              </div>
            </div>
          </div>

          {/* Search and Add Query */}
          <div className="flex flex-row items-center justify-between mb-0">
            <div className="text-base text-gray-700 font-medium" style={{ fontSize: '0.75em' }}>
              <span className="font-semibold">Query Raised By:</span>
              {activeStaffNames.length > 0 ? (
                activeStaffNames.map((name, idx) => (
                  <span key={name} className="ml-2">
                    <span className="font-bold">{staffQueryCounts[name] || 0}</span> {name}{idx < activeStaffNames.length - 1 && ' |'}
                  </span>
                ))
              ) : (
                <span className="ml-2 text-gray-400">No data</span>
              )}
            </div>
            <div className="flex items-center gap-4">
              <input
                type="text"
                placeholder="Search..."
                className="input-field w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-xs"
                value={searchMobile}
                onChange={(e) => setSearchMobile(e.target.value)}
              />
              <button
                className="btn-primary bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow-md transition duration-200"
                style={{ minWidth: 140, width: 140 }}
                onClick={() => setShowAddQuery(true)}
              >
                Add Query
              </button>
            </div>
          </div>

          {/* Query List */}
          <div className="mt-4">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-primary-50">
                  <tr>
                    {user?.role === 'admin' && (
                      <th className="px-2 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Created By</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">User ID</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">User Info</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">KYC Status</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Query</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Response</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Remarks</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Updated By</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-primary-700 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {filteredQueries.map((query) => (
                    <tr key={query.id} className="hover:bg-primary-50/50 transition">
                      {user?.role === 'admin' && (
                        <td className="px-2 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={selectedQueries.includes(query.id)}
                            onChange={() => handleSelectQuery(query.id)}
                          />
                        </td>
                      )}
                      {/* Created By */}
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <div className="text-sm text-gray-500">{formatLocal(query.createdAt)}</div>
                        <div className="text-xs text-gray-500">{query.createdBy?.name || '-'}</div>
                      </td>
                      {/* User ID */}
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <div className="font-semibold text-gray-800">{(query as any).userMobileNumber}</div>
                        <div className="text-xs text-gray-500">{capitalizeFirst((query as any).city) || '-'}</div>
                      </td>
                      {/* User Info */}
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <div className="font-semibold text-gray-800">{capitalizeWords((query as any).userName) || '-'}</div>
                        <div className="text-xs text-gray-500">{capitalizeWords((query as any).userType) || '-'}</div>
                      </td>
                      {/* KYC Status */}
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <div className="text-xs">EKYC: {getEkycStatusLabel((query as any).ekycStatus || 'notinitiated')}</div>
                        <div className="text-xs">PKYC: {getPkycStatusLabel((query as any).pkycStatus || 'notinitiated')}</div>
                      </td>
                      {/* Query */}
                      <td className="px-4 py-3 align-top max-w-xs">
                        <div className="truncate-2-lines text-xs" title={query.query}>{capitalizeAfterDot(query.query)}</div>
                      </td>
                      {/* Response */}
                      <td className="px-4 py-3 align-top max-w-xs">
                        <div className="truncate-2-lines text-xs" title={query.response}>{capitalizeAfterDot(query.response)}</div>
                      </td>
                      {/* Remarks */}
                      <td className="px-4 py-3 align-top max-w-xs">
                        <div className="truncate-2-lines text-xs" title={query.remarks}>{capitalizeAfterDot(query.remarks)}</div>
                      </td>
                      {/* Status */}
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <span className={`inline-flex items-center font-bold px-2 py-1 rounded-full text-xs ${
                          query.status === 'resolved' ? 'bg-green-100 text-green-700' :
                          query.status === 'unsolved' ? 'bg-red-100 text-red-700' :
                          query.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          query.status === 'enquiry' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {query.status === 'resolved' && <svg className="w-4 h-4 mr-1 text-green-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>}
                          {query.status === 'unsolved' && <svg className="w-4 h-4 mr-1 text-red-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>}
                          {query.status === 'pending' && <svg className="w-4 h-4 mr-1 text-yellow-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" /></svg>}
                          {query.status === 'enquiry' && <svg className="w-4 h-4 mr-1 text-blue-500" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M12 8v4l3 3" /></svg>}
                          {query.status.charAt(0).toUpperCase() + query.status.slice(1)}
                        </span>
                      </td>
                      {/* Updated By */}
                      <td className="px-4 py-3 align-top whitespace-nowrap">
                        <div className="text-sm text-gray-500">{formatLocal(query.updatedAt)}</div>
                        <div className="text-xs text-gray-500">{query.updatedBy?.name || '-'}</div>
                      </td>
                      {/* Action */}
                      <td className="px-4 py-3 align-top">
                        <button
                          className="text-primary-600 hover:text-primary-800"
                          onClick={() => openEditModal(query)}
                          title="Edit Query"
                        >
                          <PencilSquareIcon className="w-5 h-5" />
                        </button>
                        {user?.role === 'admin' && (
                          <button
                            className="ml-2 text-red-600 hover:text-red-800"
                            onClick={() => handleDelete(query.id)}
                            title="Delete Query"
                          >
                            <TrashIcon className="w-5 h-5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination and Footer Row */}
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center text-xs text-gray-500">
                Made With <span className="mx-1 text-red-500">♥</span> By <span className="font-bold ml-1">DigiCASH INDIA</span>
              </div>
              <div className="flex items-center gap-2">
                {user?.role === 'admin' && (
                  <button
                    className="btn-danger text-xs px-3 py-1 rounded disabled:opacity-50"
                    disabled={selectedQueries.length === 0}
                    onClick={handleBulkDelete}
                  >
                    Delete Selected
                  </button>
                )}
                <span className="text-xs">Rows per page:</span>
                <select value={rowsPerPage} onChange={e => { setRowsPerPage(Number(e.target.value)); setPage(1); }} className="input-field text-xs w-20">
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-xs">Page {page} of {totalPages}</span>
                <button disabled={page === 1} onClick={() => setPage(page - 1)} className="btn-secondary text-xs px-2 py-1">Prev</button>
                <button disabled={page === totalPages} onClick={() => setPage(page + 1)} className="btn-secondary text-xs px-2 py-1">Next</button>
              </div>
            </div>
          </div>
        </main>
      </div>
      <AddQueryModal
        isOpen={showAddQuery}
        onClose={() => setShowAddQuery(false)}
        onSuccess={() => { fetchQueries(); fetchCommonQueries(); fetchSummary(); }}
        commonQueries={commonQueries}
      />
      <EditQueryModal
        isOpen={editModalOpen && !!editQuery}
        onClose={() => setEditModalOpen(false)}
        query={editQuery}
        user={user}
        onSave={handleEditSave}
        onDelete={handleDelete}
      />
      {/* Staff Management Modal */}
      {showStaffMgmt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl p-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Staff Management</h2>
              <button onClick={() => setShowStaffMgmt(false)} className="text-gray-400 hover:text-gray-500">✕</button>
            </div>
            <form className="flex gap-2 mb-4" onSubmit={async e => {
              e.preventDefault();
              const name = (e.target as any).name.value;
              const mobileNumber = (e.target as any).mobileNumber.value;
              const password = (e.target as any).password.value;
              const role = (e.target as any).role.value;
              try {
                const token = sessionStorage.getItem('token');
                await api.post('/staff', { name, mobileNumber, password, role }, { headers: { Authorization: `Bearer ${token}` } });
                toast.success('Staff added');
                playSuccessSound();
                fetchStaffList();
                (e.target as any).reset();
              } catch (error: any) {
                toast.error(error.response?.data?.message || 'Failed to add staff');
                playErrorSound();
              }
            }}>
              <input name="name" placeholder="Name" className="input-field text-xs" required />
              <input name="mobileNumber" placeholder="Mobile" className="input-field text-xs" required maxLength={10} pattern="[0-9]{10}" />
              <input name="password" placeholder="Password" className="input-field text-xs" required />
              <select name="role" className="input-field text-xs"><option value="staff">Staff</option><option value="admin">Admin</option></select>
              <button type="submit" className="btn-primary text-xs">Add</button>
            </form>
            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full text-xs mb-2"><thead><tr><th>Name</th><th>Mobile</th><th>Role</th><th>Status</th><th>Action</th></tr></thead><tbody>
                {staffList.map(staff => (
                  <tr key={staff._id}>
                    <td>{editingStaff?._id === staff._id ? <input value={editingStaff.name} onChange={e => setEditingStaff({ ...editingStaff, name: e.target.value })} className="input-field text-xs" /> : staff.name}</td>
                    <td>{editingStaff?._id === staff._id ? <input value={editingStaff.mobileNumber} onChange={e => setEditingStaff({ ...editingStaff, mobileNumber: e.target.value })} className="input-field text-xs" /> : staff.mobileNumber}</td>
                    <td>{editingStaff?._id === staff._id ? <select value={editingStaff.role} onChange={e => setEditingStaff({ ...editingStaff, role: e.target.value })} className="input-field text-xs"><option value="staff">Staff</option><option value="admin">Admin</option></select> : staff.role}</td>
                    <td>{staff.isActive ? 'Active' : 'Inactive'}</td>
                    <td>
                      {editingStaff?._id === staff._id ? (
                        <>
                          <button className="btn-primary text-xs mr-1" onClick={async () => {
                            try {
                              const token = sessionStorage.getItem('token');
                              await api.patch(`/staff/${staff._id}`, editingStaff, { headers: { Authorization: `Bearer ${token}` } });
                              toast.success('Staff updated');
                              playSuccessSound();
                              setEditingStaff(null);
                              fetchStaffList();
                            } catch (error: any) {
                              toast.error(error.response?.data?.message || 'Failed to update staff');
                              playErrorSound();
                            }
                          }}>Save</button>
                          <button className="btn-secondary text-xs" onClick={() => setEditingStaff(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button className="btn-secondary text-xs mr-1" onClick={() => setEditingStaff(staff)}>Edit</button>
                          <button className="btn-secondary text-xs text-red-600 mr-1" onClick={async () => {
                            if (!window.confirm('Are you sure you want to delete this staff member?')) return;
                            try {
                              const token = sessionStorage.getItem('token');
                              await api.delete(`/staff/${staff._id}`, { headers: { Authorization: `Bearer ${token}` } });
                              toast.success('Staff deleted');
                              playSuccessSound();
                              fetchStaffList();
                            } catch (error: any) {
                              toast.error(error.response?.data?.message || 'Failed to delete staff');
                              playErrorSound();
                            }
                          }}>Delete</button>
                          <button className="btn-secondary text-xs" onClick={async () => {
                            try {
                              const token = sessionStorage.getItem('token');
                              await api.patch(`/staff/${staff._id}/status`, { isActive: !staff.isActive }, { headers: { Authorization: `Bearer ${token}` } });
                              toast.success(`Staff ${staff.isActive ? 'deactivated' : 'activated'}`);
                              playSuccessSound();
                              fetchStaffList();
                            } catch (error: any) {
                              toast.error(error.response?.data?.message || 'Failed to update staff status');
                              playErrorSound();
                            }
                          }}>{staff.isActive ? 'Deactivate' : 'Activate'}</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          </div>
        </div>
      )}
      {/* QnR Management Modal */}
      {showQRMgmt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl p-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">QnR Management (Common Queries & Responses)</h2>
              <button onClick={() => setShowQRMgmt(false)} className="text-gray-400 hover:text-gray-500">✕</button>
            </div>
            <form className="flex gap-2 mb-4" onSubmit={async e => {
              e.preventDefault();
              try {
                const token = sessionStorage.getItem('token');
                if (editingCQ) {
                  await api.put(`/common-queries/${editingCQ._id}`, { query: newCommonQuery, response: newCommonResponse }, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success('Query updated');
                  playSuccessSound();
                } else {
                  await api.post('/common-queries', { query: newCommonQuery, response: newCommonResponse }, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success('Query added');
                  playSuccessSound();
                }
                setNewCommonQuery('');
                setNewCommonResponse('');
                setEditingCQ(null);
                fetchCommonQueries();
              } catch (error: any) {
                toast.error(error.response?.data?.message || 'Failed to save query');
                playErrorSound();
              }
            }}>
              <input value={newCommonQuery} onChange={e => setNewCommonQuery(e.target.value)} placeholder="Query" className="input-field text-xs flex-1" required />
              <input value={newCommonResponse} onChange={e => setNewCommonResponse(e.target.value)} placeholder="Response" className="input-field text-xs flex-1" required />
              <button type="submit" className="btn-primary text-xs">{editingCQ ? 'Update' : 'Add'}</button>
              {editingCQ && <button type="button" className="btn-secondary text-xs" onClick={() => { setEditingCQ(null); setNewCommonQuery(''); setNewCommonResponse(''); }}>Cancel</button>}
            </form>
            <div className="mb-2 flex items-center gap-2">
              <input type="file" accept=".xlsx,.xls,.csv" className="input-field text-xs" onChange={async e => {
                const file = e.target.files?.[0];
                if (!file) return;
                try {
                  const data = await file.arrayBuffer();
                  const wb = XLSX.read(data);
                  const ws = wb.Sheets[wb.SheetNames[0]];
                  const rows = XLSX.utils.sheet_to_json(ws);
                  const bulk = (rows as any[]).map(r => ({ query: r.Query || r.query, response: r.Response || r.response }));
                  const token = sessionStorage.getItem('token');
                  await api.post('/common-queries/bulk', { data: bulk }, { headers: { Authorization: `Bearer ${token}` } });
                  toast.success('Bulk queries added');
                  playSuccessSound();
                  fetchCommonQueries();
                } catch (error: any) {
                  toast.error(error.response?.data?.message || 'Failed to upload file');
                  playErrorSound();
                }
              }} />
              <span className="text-xs ml-2">Upload Excel to add multiple</span>
            </div>
            <div className="overflow-x-auto max-h-96">
              <table className="min-w-full text-xs"><thead><tr><th>Query</th><th>Response</th><th>Action</th></tr></thead><tbody>
                {commonQueries.map((item, idx) => (
                  <tr key={item._id || idx}>
                    <td>{item.query}</td>
                    <td>{item.response}</td>
                    <td>
                      <button className="btn-secondary text-xs mr-1" onClick={() => { setEditingCQ(item as any); setNewCommonQuery(item.query); setNewCommonResponse(item.response); }}>Edit</button>
                      <button className="btn-secondary text-xs text-red-600" onClick={async () => {
                        if (!window.confirm('Are you sure you want to delete this query?')) return;
                        try {
                          const token = sessionStorage.getItem('token');
                          await api.delete(`/common-queries/${item._id}`, { headers: { Authorization: `Bearer ${token}` } });
                          toast.success('Query deleted');
                          playSuccessSound();
                          fetchCommonQueries();
                        } catch (error: any) {
                          toast.error(error.response?.data?.message || 'Failed to delete query');
                          playErrorSound();
                        }
                      }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody></table>
            </div>
          </div>
        </div>
      )}
      <Setup2FAModal isOpen={show2FAModal} onClose={() => setShow2FAModal(false)} />
      {showDOBPrompt && <DOBPromptModal open={showDOBPrompt} onSubmit={handleDOBSubmit} />}

    </div>
  );
};

export default Dashboard;

const DOBPromptModal: React.FC<{ open: boolean; onSubmit: (dob: string) => void; }> = ({ open, onSubmit }) => {
  const { user } = useAuth();
  const [dob, setDob] = useState('');
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-sm flex flex-col items-center">
        <h2 className="text-2xl font-bold mb-2">Hi, {user?.name || user?.mobileNumber || 'User'}</h2>
        <p className="text-base text-gray-700 mb-6 text-center">Your date of birth is missing in the record.<br/>Please update it below to continue.</p>
        <input
          type="date"
          className="border rounded px-3 py-2 mb-4 w-full"
          value={dob}
          onChange={e => setDob(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
        />
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded w-full"
          onClick={() => dob && onSubmit(dob)}
          disabled={!dob}
        >
          Save DOB
        </button>
      </div>
    </div>
  );
}; 